import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    Typography,
    Collapse,
    Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { addChatsToAiTestContainer, getAiTestContainerById } from "../../api/AiAgentApi";
import AddIcon from "@mui/icons-material/Add";
import { AiTestContainerModelsForm } from "./AiTestContainerModelsForm";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LaunchIcon from '@mui/icons-material/Launch';

export default function AiTestContainerDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [container, setContainer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [addChatsOpen, setAddChatsOpen] = useState(false);
    const [newChats, setNewChats] = useState([
        {
            modelName: '',
            modelProvider: 'OPEN_AI',
        },
    ]);

    // Управление открытия деталей резерва по chat id
    const [openReservationDetails, setOpenReservationDetails] = useState({});

    useEffect(() => {
        loadContainer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    async function loadContainer() {
        try {
            setLoading(true);
            setError(null);
            const data = await getAiTestContainerById(id);
            setContainer(data);
        } catch (e) {
            console.error(e);
            setError('Не удалось загрузить контейнер');
        } finally {
            setLoading(false);
        }
    }

    function handleBack() {
        navigate('/ai-test-containers');
    }

    // Функция для получения цвета статуса
    const getStatusColor = (status) => {
        switch (status) {
            case 'COMPLETED':
                return 'success';
            case 'PROCESSING':
                return 'warning';
            case 'CREATED':
                return 'default';
            default:
                return 'default';
        }
    };

    // Функция для получения текста статуса на русском
    const getStatusText = (status) => {
        switch (status) {
            case 'COMPLETED':
                return 'Завершено';
            case 'PROCESSING':
                return 'В процессе';
            case 'CREATED':
                return 'Создано';
            default:
                return status;
        }
    };

    if (loading && !container) {
        return (
            <Box p={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={24} />
                    <Typography>Загрузка...</Typography>
                </Stack>
            </Box>
        );
    }

    if (error && !container) {
        return (
            <Box p={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography color="error">{error}</Typography>
                    <Button onClick={loadContainer}>Повторить</Button>
                </Stack>
            </Box>
        );
    }

    if (!container) {
        return null;
    }

    const chats = container.chats || [];

    async function handleSubmitAddChats() {
        try {
            const hasEmpty = newChats.some(
                (m) => !m.modelName || !m.modelProvider
            );
            if (hasEmpty) {
                alert('Заполните все модели');
                return;
            }

            await addChatsToAiTestContainer(container.id, newChats);
            setAddChatsOpen(false);

            await loadContainer();

            setNewChats([
                { modelName: '', modelProvider: 'OPEN_AI' }
            ]);
        } catch (e) {
            console.error(e);
            alert('Не удалось добавить чаты');
        }
    }

    // Форматирование текста с переносами
    const renderMessageText = (text) => {
        if (!text) return null;
        return (
            <Typography
                variant="body2"
                sx={{
                    whiteSpace: 'pre-line',
                    wordBreak: 'break-word',
                }}
            >
                {text}
            </Typography>
        );
    };

    // Форматирование лог-текста
    const renderLogText = (text) => {
        if (!text) return null;
        return (
            <Typography
                component="span"
                variant="caption"
                sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {text}
            </Typography>
        );
    };

    // Переход к странице резерва (если у вас есть маршрут /reservations/:id)
    const handleOpenReservation = (reservationId) => {
        if (!reservationId && container?.reservationId) {
            // fallback (не обязательно)
            return;
        }
        navigate(`/reservations/${reservationId}`);
    };

    return (
        <Box p={3} sx={{ height: '100%', boxSizing: 'border-box' }}>
            {/* Шапка */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <IconButton onClick={handleBack}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" fontWeight={600}>
                        AI Test Container #{container.id}
                    </Typography>
                </Stack>

                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setAddChatsOpen(true)}
                >
                    Добавить чаты
                </Button>
            </Stack>

            <Typography variant="subtitle1" gutterBottom>
                ReservationId: <b>{container.reservationId}</b>
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Основной блок с чатами в колонках */}
            {chats.length === 0 ? (
                <Typography>В этом контейнере пока нет чатов</Typography>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                        overflowX: 'auto',
                        pb: 2,
                    }}
                >
                    {chats.map((chat, cIndex) => {
                        const reservation = chat.reservation;
                        const hasReservation = !!reservation;
                        const reservationTimestamp = reservation?.timestamp
                            ? new Date(reservation.timestamp).toLocaleString()
                            : null;

                        return (
                            <Paper
                                key={chat.id ?? cIndex}
                                variant="outlined"
                                sx={{
                                    minWidth: 320,
                                    maxWidth: 380,
                                    flex: '0 0 auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxHeight: 'calc(100vh - 180px)',
                                }}
                            >
                                {/* Заголовок колонки */}
                                <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Chat #{chat.id ?? cIndex + 1}
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Chip
                                                label={getStatusText(chat.status)}
                                                size="small"
                                                color={getStatusColor(chat.status)}
                                                variant="outlined"
                                            />
                                            {hasReservation && (
                                                <Tooltip title="Детали резерва">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setOpenReservationDetails(prev => ({
                                                            ...prev,
                                                            [chat.id ?? cIndex]: !prev[chat.id ?? cIndex]
                                                        }))}
                                                    >
                                                        <InfoOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </Stack>

                                    <Stack direction="column" spacing={0.5} mt={1}>
                                        <Typography variant="body2">
                                            modelName: <b>{chat.modelName}</b>
                                        </Typography>
                                        <Typography variant="body2">
                                            modelProvider: <b>{chat.modelProvider}</b>
                                        </Typography>
                                        {chat.status === 'PROCESSING' && (
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <CircularProgress size={12} />
                                                <Typography variant="caption" color="text.secondary">
                                                    Обработка...
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>

                                    {/* Свернутая панель с небольшим резюме резерва */}
                                    {hasReservation && (
                                        <Collapse in={!!openReservationDetails[chat.id ?? cIndex]} timeout="auto" unmountOnExit>
                                            <Box mt={1}>
                                                <Divider sx={{ my: 1 }} />
                                                <Stack spacing={0.5}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography variant="subtitle2">
                                                            Имя: {reservation?.name ?? '—'}
                                                        </Typography>
                                                    </Stack>

                                                    <Typography variant="body2">Телефон: <b>{reservation?.phone ?? '—'}</b></Typography>
                                                    <Typography variant="body2">Зона: <b>{reservation?.zone ?? '—'}</b></Typography>
                                                    <Typography variant="body2">Персон: <b>{reservation?.persons ?? '—'}</b></Typography>
                                                    <Typography variant="body2">Кальян: <b>{reservation?.hookah ? 'Да' : 'Нет'}</b></Typography>
                                                    {reservation?.comment && (
                                                        <Typography variant="body2">Комментарий: <b>{reservation.comment}</b></Typography>
                                                    )}
                                                    {reservationTimestamp && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Время: {reservationTimestamp}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Box>
                                        </Collapse>
                                    )}
                                </Box>

                                {/* Сообщения, вертикальный скролл */}
                                <Box
                                    sx={{
                                        p: 2,
                                        overflowY: 'auto',
                                        flexGrow: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                    }}
                                >
                                    {(!chat.messages || chat.messages.length === 0) && (
                                        <Box textAlign="center" py={2}>
                                            <Typography variant="body2" color="text.secondary">
                                                Сообщений пока нет
                                            </Typography>
                                            {chat.status === 'CREATED' && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Чат создан, но диалог не начат
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    {chat.messages?.map((m, mIndex) => {
                                        // Лог-сообщения рендерим отдельно как централизованный блок
                                        if (m.role === 'LOG') {
                                            return (
                                                <Box
                                                    key={m.id ?? `log-${mIndex}`}
                                                    sx={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        my: 1,
                                                    }}
                                                >
                                                    <Paper
                                                        elevation={0}
                                                        variant="outlined"
                                                        sx={{
                                                            px: 2,
                                                            py: 0.7,
                                                            borderRadius: 1,
                                                            bgcolor: 'grey.100',
                                                            maxWidth: '100%',
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <Stack direction="column" spacing={0.5} alignItems="center">
                                                            {renderLogText(m.messageText)}
                                                            {m.createdAt && (
                                                                <Typography component="span" variant="caption" color="text.secondary">
                                                                    {new Date(m.createdAt).toLocaleTimeString()}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Paper>
                                                </Box>
                                            );
                                        }

                                        // Обычные сообщения BOT / USER
                                        const isBot = m.role === 'BOT';

                                        return (
                                            <Stack
                                                key={m.id ?? `msg-${mIndex}`}
                                                direction="row"
                                                justifyContent={isBot ? 'flex-end' : 'flex-start'}
                                            >
                                                <Box
                                                    sx={{
                                                        maxWidth: '80%',
                                                        display: 'inline-block',
                                                    }}
                                                >
                                                    {/* Метаданные над пузырём (роль + время) */}
                                                    <Stack
                                                        direction="row"
                                                        justifyContent={isBot ? 'flex-end' : 'flex-start'}
                                                        sx={{ mb: 0.5 }}
                                                    >
                                                        <Typography variant="caption" color="text.secondary">
                                                            {isBot ? 'BOT' : 'USER'} ·{' '}
                                                            {m.createdAt ? new Date(m.createdAt).toLocaleTimeString() : ''}
                                                        </Typography>
                                                    </Stack>

                                                    {/* Пузырь сообщения */}
                                                    <Box
                                                        sx={{
                                                            px: 1.5,
                                                            py: 1,
                                                            borderRadius: 3,
                                                            bgcolor: isBot ? 'primary.main' : 'grey.200',
                                                            color: isBot ? 'primary.contrastText' : 'text.primary',
                                                            borderTopRightRadius: isBot ? 0 : 24,
                                                            borderTopLeftRadius: isBot ? 24 : 0,
                                                            boxShadow: 1,
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {renderMessageText(m.messageText)}
                                                    </Box>
                                                </Box>
                                            </Stack>
                                        );
                                    })}
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>
            )}

            <Dialog
                open={addChatsOpen}
                onClose={() => setAddChatsOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Добавить чаты в контейнер #{container.id}</DialogTitle>
                <DialogContent dividers>
                    <AiTestContainerModelsForm
                        models={newChats}
                        onModelsChange={setNewChats}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddChatsOpen(false)}>Отмена</Button>
                    <Button variant="contained" onClick={handleSubmitAddChats}>
                        Добавить
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
